import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  Collapse,
  IconButton,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from "@mui/material";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import { styled, useTheme } from "@mui/material/styles";
import { Form, Formik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import * as Yup from "yup";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import FormikTextField from "../FormikTextField";
import MessageVariablesPicker from "../MessageVariablesPicker";
import WithSkeleton from "../WithSkeleton";

const MSG_TYPES = [
  { value: "text", label: "Texto", icon: <TextFieldsIcon fontSize="small" />, color: "#1976d2" },
  { value: "audio", label: "Áudio", icon: <AudiotrackIcon fontSize="small" />, color: "#2e7d32" },
  { value: "image", label: "Imagem", icon: <ImageIcon fontSize="small" />, color: "#0288d1" },
  { value: "video", label: "Vídeo", icon: <VideoLibraryIcon fontSize="small" />, color: "#ed6c02" },
  { value: "file", label: "Arquivo", icon: <AttachFileIcon fontSize="small" />, color: "#7b1fa2" },
];

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogTitle-root': {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    '& .MuiTypography-root': {
      fontWeight: 500,
    },
    padding: theme.spacing(2),
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(3),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(2),
    backgroundColor: '#f5f5f5',
  },
}));

const FormContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  gap: theme.spacing(2.5),
}));

const FieldContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  marginBottom: theme.spacing(0.5),
}));

const InfoBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.1)' : '#e3f2fd',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.3)' : '#90caf9'}`,
  borderRadius: theme.spacing(1),
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const ExampleBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : '#f5f5f5',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(0.5),
  padding: theme.spacing(1.5),
  marginTop: theme.spacing(1),
  fontFamily: 'monospace',
  fontSize: '0.875rem',
  whiteSpace: 'pre-wrap',
  color: theme.palette.text.primary,
}));

const TypeBadge = styled(Box)(({ color }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '2px 8px',
  borderRadius: 12,
  backgroundColor: color + '20',
  color: color,
  fontSize: '0.75rem',
  fontWeight: 600,
}));

const QuickAnswersModal = ({
  open,
  onClose,
  onSave,
  quickAnswerId,
  initialValues
}) => {
  const initialState = {
    shortcut: "",
    message: "",
    mediaPath: "",
    type: "text",
    productId: "",
    scriptId: "",
    category: "",
    categoryId: "",
  };
  const { t } = useTranslation();
  const theme = useTheme();
  const isMounted = useRef(true);
  const messageInputRef = useRef();
  const [loading, setLoading] = useState(false);
  const [quickAnswer, setQuickAnswer] = useState(initialState);
  const [showHelp, setShowHelp] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [products, setProducts] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [msgType, setMsgType] = useState("text");
  const fileInputRef = useRef(null);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (open) {
      loadProducts();
      loadCategories();
    }
  }, [open]);

  const loadProducts = async () => {
    try {
      const [prodRes, scrRes] = await Promise.all([
        api.get('/products'),
        api.get('/scripts'),
      ]);
      if (isMounted.current) {
        setProducts(prodRes.data || []);
        setScripts(scrRes.data.scripts || scrRes.data || []);
      }
    } catch (_) {}
  };

  const loadCategories = async (pid) => {
    try {
      const url = pid ? `/categories?productId=${pid}` : '/categories';
      const { data } = await api.get(url);
      if (isMounted.current) setCategories(data || []);
    } catch (_) {}
  };

  useEffect(() => {
    if (quickAnswerId) {
      const fetchQuickAnswer = async () => {
        setLoading(true);
        try {
          const { data } = await api.get(`/quickAnswers/${quickAnswerId}`);
          if (isMounted.current) {
            setQuickAnswer(data);
            setMsgType(data.type || "text");

            if (data.mediaPath) {
              setFilePreview({
                url: `${process.env.REACT_APP_BACKEND_URL}/public/${data.mediaPath}`,
                name: data.mediaPath,
                type: 'application/octet-stream',
                size: 0
              });
            }
          }
        } catch (err) {
          toastError(err);
        } finally {
          setLoading(false);
        }
      };

      fetchQuickAnswer();
    }
  }, [quickAnswerId]);

  useEffect(() => {
    if (open && messageInputRef.current) {
      messageInputRef.current.focus();
    }
  }, [open]);

  const handleClose = () => {
    onClose();
    setQuickAnswer(initialState);
    setSelectedFile(null);
    setFilePreview(null);
    setLoading(false);
    setMsgType("text");
  };

  const handleSaveQuickAnswer = async values => {
    try {
      const formData = new FormData();
      formData.append("shortcut", values.shortcut);
      formData.append("message", values.message || "");
      formData.append("type", msgType);
      if (values.productId) formData.append("productId", values.productId);
      if (values.categoryId) formData.append("categoryId", values.categoryId);
      if (values.category) formData.append("category", values.category);
      if (values.scriptId) formData.append("scriptId", values.scriptId);

      if (selectedFile) {
        formData.append("media", selectedFile);
      }

      if (quickAnswerId) {
        await api.put(`/quickAnswers/${quickAnswerId}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        onClose();
      } else {
        const { data } = await api.post("/quickAnswers", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (onSave) {
          onSave(data);
        }
        onClose();
      }
      toast.success(t("quickAnswersModal.success"));
    } catch (err) {
      toastError(err, t);
    }
  };

  const handleClickMsgVar = (msgVar, setValueFunc) => {
    const el = messageInputRef.current;
    const firstHalfText = el.value.substring(0, el.selectionStart);
    const secondHalfText = el.value.substring(el.selectionEnd);

    setValueFunc("message", `${firstHalfText}${msgVar}${secondHalfText}`);

    const newCursorPos = el.selectionStart + msgVar.length;
    setTimeout(() => {
      el.setSelectionRange(newCursorPos, newCursorPos);
      el.focus();
    }, 100);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);

      // Auto-detect type from file
      if (file.type.startsWith('audio/')) setMsgType('audio');
      else if (file.type.startsWith('video/')) setMsgType('video');
      else if (file.type.startsWith('image/')) setMsgType('image');
      else if (msgType === 'text') setMsgType('file');

      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreview({
          url: reader.result,
          name: file.name,
          type: file.type,
          size: file.size
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const getFileIcon = (type) => {
    if (type.startsWith('image/')) return <ImageIcon />;
    if (type.startsWith('video/')) return <VideoLibraryIcon />;
    if (type.startsWith('audio/')) return <AudiotrackIcon />;
    return <InsertDriveFileIcon />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const isTextType = msgType === 'text';

  // Dynamic validation based on type
  const QuickAnswerSchema = Yup.object().shape({
    shortcut: Yup.string()
      .min(2, "Too Short!")
      .max(50, "Too Long!")
      .required("Required"),
    message: isTextType
      ? Yup.string().min(1, "Obrigatório para tipo texto").max(30000, "Too Long!").required("Required")
      : Yup.string().max(30000, "Too Long!").nullable(),
  });

  const currentTypeInfo = MSG_TYPES.find(t => t.value === msgType) || MSG_TYPES[0];

  return (
    <StyledDialog
      maxWidth="sm"
      fullWidth
      open={open}
      onClose={handleClose}
      scroll="paper"
    >
      <DialogTitle>
        {quickAnswerId
          ? t("quickAnswersModal.title.edit")
          : t("quickAnswersModal.title.add")}
      </DialogTitle>
      <Formik
        initialValues={quickAnswer || initialState}
        enableReinitialize={true}
        validationSchema={QuickAnswerSchema}
        onSubmit={handleSaveQuickAnswer}
      >
        {({ touched, errors, isSubmitting, setFieldValue, values }) => (
          <Form>
            <DialogContent dividers>
              <FormContainer>

                {/* Type Selector */}
                <FieldContainer>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 0.5 }}>
                    Tipo de Mensagem
                  </Typography>
                  <ToggleButtonGroup
                    value={msgType}
                    exclusive
                    onChange={(_, val) => { if (val) setMsgType(val); }}
                    size="small"
                    fullWidth
                    sx={{
                      '& .MuiToggleButton-root': {
                        flex: 1,
                        py: 0.8,
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        gap: 0.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        '&.Mui-selected': {
                          backgroundColor: currentTypeInfo.color + '20',
                          borderColor: currentTypeInfo.color,
                          color: currentTypeInfo.color,
                        },
                      },
                    }}
                  >
                    {MSG_TYPES.map(t => (
                      <ToggleButton key={t.value} value={t.value}>
                        {t.icon}
                        {t.label}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </FieldContainer>

                {/* Shortcut */}
                <FieldContainer>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    {t("quickAnswersModal.form.shortcut")}
                  </Typography>
                  <WithSkeleton loading={loading}>
                    <FormikTextField
                      autoFocus
                      name="shortcut"
                      touched={touched}
                      errors={errors}
                      variant="outlined"
                      fullWidth
                      value={values.shortcut || ""}
                      onChange={(e) => setFieldValue("shortcut", e.target.value)}
                      disabled={isSubmitting}
                      placeholder={t("quickAnswersModal.form.shortcut")}
                    />
                  </WithSkeleton>
                </FieldContainer>


                {/* Product Selector */}
                {products.length > 0 && (
                  <FieldContainer>
                    <FormControl fullWidth size="small">
                      <InputLabel>Produto (opcional)</InputLabel>
                      <Select
                        value={values.productId || ""}
                        label="Produto (opcional)"
                        onChange={(e) => {
                          const pid = e.target.value;
                          setFieldValue("productId", pid);
                          setFieldValue("categoryId", "");
                          setFieldValue("category", "");
                          loadCategories(pid);
                        }}
                        disabled={isSubmitting}
                      >
                        <MenuItem value="">Nenhum produto</MenuItem>
                        {products.map(p => (
                          <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </FieldContainer>
                )}

                {/* Category */}
                <FieldContainer>
                  <FormControl fullWidth size="small">
                    <InputLabel>Categoria (opcional)</InputLabel>
                    <Select
                      value={values.categoryId || ""}
                      label="Categoria (opcional)"
                      onChange={(e) => {
                        const catId = e.target.value;
                        setFieldValue("categoryId", catId);
                        const cat = categories.find(c => String(c.id) === String(catId));
                        setFieldValue("category", cat ? cat.name : "");
                      }}
                      disabled={isSubmitting}
                    >
                      <MenuItem value="">Sem categoria</MenuItem>
                      {categories.map(c => (
                        <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </FieldContainer>
                {/* Message */}
                <FieldContainer>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">
                      {isTextType
                        ? t("quickAnswersModal.form.message")
                        : "Legenda (opcional)"}
                    </Typography>
                    {isTextType && (
                      <Button
                        size="small"
                        startIcon={<InfoOutlinedIcon />}
                        endIcon={<ExpandMoreIcon sx={{ transform: showHelp ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }} />}
                        onClick={() => setShowHelp(!showHelp)}
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          color: theme.palette.primary.main
                        }}
                      >
                        Como dividir mensagens
                      </Button>
                    )}
                  </Box>

                  {isTextType && (
                    <Collapse in={showHelp}>
                      <InfoBox>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: theme.palette.primary.main }}>
                          💡 Divisão Automática de Mensagens
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.6 }}>
                          Use <strong>|q</strong> para dividir sua mensagem em partes que serão enviadas com delay automático.
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          ⏱️ Delay por quantidade de pipes:
                        </Typography>
                        <Typography variant="body2" component="div" sx={{ mb: 1.5, pl: 2 }}>
                          • <strong>|q</strong> = 4 segundos<br/>
                          • <strong>||q</strong> = 8 segundos<br/>
                          • <strong>|||q</strong> = 12 segundos
                        </Typography>
                        <ExampleBox>
                          Olá, tudo bem? |q{"\n"}Seu pedido foi processado! ||q{"\n"}Obrigado pela preferência!
                        </ExampleBox>
                      </InfoBox>
                    </Collapse>
                  )}

                  <WithSkeleton fullWidth loading={loading}>
                    <FormikTextField
                      multiline
                      inputRef={messageInputRef}
                      minRows={isTextType ? 5 : 2}
                      fullWidth
                      name="message"
                      touched={touched}
                      errors={errors}
                      variant="outlined"
                      value={values.message || ""}
                      onChange={(e) => setFieldValue("message", e.target.value)}
                      disabled={isSubmitting}
                      placeholder={isTextType
                        ? t("quickAnswersModal.form.message")
                        : "Legenda da mídia (opcional)"}
                    />
                  </WithSkeleton>
                </FieldContainer>

                {/* File Attachment */}
                <FieldContainer>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">
                      {isTextType ? "Anexar Arquivo (Opcional)" : `Arquivo de ${currentTypeInfo.label}`}
                      {!isTextType && <span style={{ color: theme.palette.error.main }}> *</span>}
                    </Typography>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={
                        msgType === 'audio' ? 'audio/*' :
                        msgType === 'image' ? 'image/*' :
                        msgType === 'video' ? 'video/*' :
                        'image/*,video/*,audio/*,.pdf,.doc,.docx,.txt,.zip,.rar'
                      }
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={currentTypeInfo.icon}
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isSubmitting}
                      sx={{
                        textTransform: 'none',
                        borderColor: currentTypeInfo.color,
                        color: currentTypeInfo.color,
                        '&:hover': { borderColor: currentTypeInfo.color, backgroundColor: currentTypeInfo.color + '10' },
                      }}
                    >
                      Selecionar {currentTypeInfo.label}
                    </Button>
                  </Box>

                  {filePreview && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 1.5,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1.5,
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : '#f8f9fa',
                      }}
                    >
                      <Avatar
                        sx={{
                          backgroundColor: currentTypeInfo.color + '20',
                          color: currentTypeInfo.color,
                          width: 36,
                          height: 36,
                        }}
                      >
                        {getFileIcon(filePreview.type)}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap fontWeight={500}>
                          {filePreview.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {formatFileSize(filePreview.size)}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={handleRemoveFile}
                        disabled={isSubmitting}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  )}
                </FieldContainer>


                {/* Script Selector (optional, use instead of product) */}
                {scripts.length > 0 && !values.productId && (
                  <FieldContainer>
                    <FormControl fullWidth size="small">
                      <InputLabel>Script vinculado (opcional)</InputLabel>
                      <Select
                        value={values.scriptId || ""}
                        label="Script vinculado (opcional)"
                        onChange={(e) => setFieldValue("scriptId", e.target.value)}
                        disabled={isSubmitting}
                      >
                        <MenuItem value="">Nenhum script</MenuItem>
                        {scripts.map(s => (
                          <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </FieldContainer>
                )}

                {/* Variables picker (only for text type) */}
                {isTextType && (
                  <WithSkeleton loading={loading}>
                    <MessageVariablesPicker
                      disabled={isSubmitting}
                      onClick={(value) => handleClickMsgVar(value, setFieldValue)}
                    />
                  </WithSkeleton>
                )}
              </FormContainer>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleClose}
                disabled={isSubmitting}
                variant="outlined"
                sx={{
                  borderRadius: 20,
                  px: 3,
                  backgroundColor: '#e0e0e0',
                  color: '#757575',
                  '&:hover': { backgroundColor: '#d5d5d5' },
                  textTransform: 'uppercase',
                  fontWeight: 'bold',
                }}
              >
                {t("quickAnswersModal.buttons.cancel")}
              </Button>
              <ButtonWithSpinner
                type="submit"
                disabled={loading || isSubmitting}
                loading={isSubmitting}
                variant="contained"
                sx={{
                  borderRadius: 20,
                  px: 3,
                  backgroundColor: theme.palette.primary.main,
                  '&:hover': { backgroundColor: theme.palette.primary.dark },
                  textTransform: 'uppercase',
                  fontWeight: 'bold',
                }}
              >
                {quickAnswerId
                  ? t("quickAnswersModal.buttons.okEdit")
                  : t("quickAnswersModal.buttons.okAdd")}
              </ButtonWithSpinner>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </StyledDialog>
  );
};

export default QuickAnswersModal;
